import type { DomainCategory, MockupCollection, TemplateType } from '../types';
import { API_BASE_URL, resolvePublicAssetUrl } from './api';
import { normalizeCollectionPurpose, scopeCollectionPurposeId, type CollectionPurpose } from '@motionpitch/template-contract';

export async function loadCollectionPurposes(): Promise<CollectionPurpose[]> {
  const url = `${API_BASE_URL.replace(/\/$/, '')}/mockups/collection-purposes.json`;
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error('Purposes unavailable');
  const value = await response.json();
  if (!Array.isArray(value)) throw new Error('Invalid purposes');
  const items = value.map(normalizeCollectionPurpose).filter((item): item is CollectionPurpose => Boolean(item))
    .map(item => ({ ...item,
      background: resolveAssetUrl(item.background, url) || '',
      autoBlurBackground: resolveAssetUrl(item.autoBlurBackground, url) || undefined
    }));
  // Visibility is server-owned. Neither empty responses nor failures may invent defaults.
  return items.filter(item=>item.published).sort((a,b)=>a.order-b.order);
}

type CollectionQueryType = TemplateType | 'MOTION';

export async function loadCollections(
  apiBaseUrlOverride?: string,
  templateType: CollectionQueryType = 'CG'
): Promise<MockupCollection[]> {
  const apiBase = (apiBaseUrlOverride || API_BASE_URL).trim().replace(/\/$/, '');
  if (!apiBase) throw new Error('Collections API unavailable');

  const url = `${apiBase}/mockups/collections.json?type=${encodeURIComponent(templateType)}`;
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Collections request failed: ${response.status}`);
  const payload = (await response.json()) as unknown;
  if (!Array.isArray(payload)) throw new Error('Invalid collections');
  return parseCollectionsPayload(payload, url);
}

export function parseCollectionsPayload(payload: unknown, baseUrl: string): MockupCollection[] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((item, index) => normalizeCollection(item, baseUrl, index))
    .filter((item): item is MockupCollection => item !== null)
    .sort((a, b) => a.order - b.order);
}

function normalizeCollection(value: unknown, baseUrl: string, fallbackOrder: number): MockupCollection | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const raw = value as Record<string, unknown>;
  const id = toString(raw.id) || toSlug(toString(raw.name));
  const name = toString(raw.name);
  if (!id || !name) {
    return null;
  }

  return {
    id,
    templateType: normalizeTemplateTypeField(raw.templateType),
    purposeId: scopeCollectionPurposeId(raw.purposeId,normalizeTemplateTypeField(raw.templateType)),
    name,
    order: toOrder(raw.order, fallbackOrder),
    published: raw.published !== false,
    listed: raw.listed !== false,
    domainCategory: normalizeDomainCategory(raw.domainCategory),
    background: resolveAssetUrl(raw.background, baseUrl) || '',
    fontColor: normalizeColor(raw.fontColor),
    autoFontColor: normalizeOptionalColor(raw.autoFontColor),
    autoBlurBackground: resolveAssetUrl(raw.autoBlurBackground, baseUrl) || undefined
  };
}

function normalizeDomainCategory(value: unknown): DomainCategory | undefined {
  if (value === 'App' || value === 'Promo' || value === 'Brand') {
    return value;
  }
  return undefined;
}


function normalizeTemplateTypeField(value: unknown): TemplateType {
  const normalized = String(value || '').trim().toUpperCase().replace(/_/g, '-');
  if (normalized === 'MOTION-AI' || normalized === 'MOTIONAI') {
    return 'MOTION-AI';
  }
  if (normalized === 'MOTION-CG' || normalized === 'MOTIONCG' || normalized === 'MOTION') {
    return 'MOTION-CG';
  }
  if (normalized === 'AI') {
    return 'AI';
  }
  return 'CG';
}

function resolveAssetUrl(value: unknown, baseUrl: string): string | null {
  const input = toString(value);
  if (!input) {
    return null;
  }

  try {
    return resolvePublicAssetUrl(input, baseUrl).toString();
  } catch {
    return null;
  }
}

function normalizeColor(value: unknown): string {
  const input = toString(value);
  if (/^#[0-9a-fA-F]{6}$/.test(input)) {
    return input.toLowerCase();
  }
  return '#ffffff';
}

function normalizeOptionalColor(value: unknown): string | undefined {
  const input = toString(value);
  return /^#[0-9a-fA-F]{6}$/.test(input) ? input.toLowerCase() : undefined;
}

function toString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toOrder(value: unknown, fallback: number): number {
  const order = Number(value);
  if (Number.isFinite(order)) {
    return Math.max(0, Math.floor(order));
  }
  return fallback;
}
